import React from 'react';
import { connect } from 'react-redux';
import { has, isEmpty, pick } from 'lodash';
import { useSqlAutomation } from 'new/hooks/useFeatureFlags';

import { FORM_TYPE_CREATE_DATA_SOURCE } from '../../constants/formTypes';
import {
  GET_ALL_DATA_MAPS_REQUEST,
  GET_ALL_CREDENTIALS_REQUEST,
  PROBE_GET_TREE_REQUEST,
  PROBE_GET_SAMPLE_REQUEST,
  PROBE_GET_WSDL_REQUEST,
  PROBE_CALL_SOAP_REQUEST,
  ADD_DATA_SOURCE_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_VENDOR_AUTH_PARAMS_REQUEST,
  GET_VENDOR_ENDPOINTS_REQUEST,
  GET_VENDOR_ENDPOINT_PARAMS_REQUEST,
  GET_FULL_VENDOR_REQUEST,
} from '../../constants/actionTypes';
import { CONNECTION_CATEGORY_REST } from '../../constants/connectionCategories';

import UIUtils from '../../lib/UIUtils';

import {
  loadDataCredentials,
  loadVendorAuths,
  getDataCredential,
  addCredentialFromForm,
  updateCredentialFromForm,
  verifyCredentialFromForm,
  verifyCredentialSilent,
  updateFormData,
  resetFormData,
  setFormError,
  setFormStageIndex,
  markFormCompleted,
  addDataSourceFromForm,
  updateDataSourceFromForm,
  updateSourceInfo,
  uploadFileToSource,
  probeGetConnectionTree,
  probeGetInitialDBTree,
  probeToggleTreeNode,
  probeGetWSDL,
  probeCallSOAP,
  loadDataMaps,
  loadDataMapSamples,
  probeGetSample,
  probeClearAll,
  probeRemoveRestStage,
  loadDataSource,
  setContextualSupport,
  unsetContextualSupport,
  loadFullVendor,
  setFormAwaitingValidation,
  createCodeContainer,
  updateCodeContainer,
  informEarlyIntent,
} from 'js/actions';

import {
  getCredentialsByType,
  getCurrentUser,
  getCurrentOrg,
  getSamplesForDataSet,
} from '../../reducers/selectors';

import NM_TEMPLATES from '../../../mocks/NMTemplateConfig';
import AddDataSourceForm from './AddDataSourceForm';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import * as OLD_PATHS from 'js/constants/pathnames';
import { bindActionCreators } from 'redux';
import { saveState } from 'new/state/hashedState/action';
import { getCurrentProjectId } from 'js/reducers/app/selectors';

const mapStateToProps = (state, ownProps) => {
  const {
    entities: { sources, dataSets, dataMaps, credentials, vendors, vendorEndpoints },
    forms,
    paging,
    credentials: { byId },
    sources: { lastFileUploaded, lastAddedId },
    probe,
    request,
    dataMaps: { byId: dataMapIds, samplesById: dataMapSamples, lastUpdated: dataMapsLastUpdated },
    routes: { previousPath },
    vendors: vendorsState,
  } = state;

  const sourceBeingEditedId = ownProps.id || UIUtils.extractParamFromURL(ownProps.match);
  const sourceBeingEdited = sourceBeingEditedId && sources[sourceBeingEditedId];

  const form = forms[FORM_TYPE_CREATE_DATA_SOURCE];
  const currentStage = form ? form.currentStage : 0;
  const formData = form ? form.data : null;
  const isFormAwaitingValidation = form ? form.awaitingValidation : false;

  const selectedConnection = has(formData, 'selectedConnection')
    ? formData.selectedConnection
    : null;

  const selectedCredential = has(formData, 'selectedCredential')
    ? formData.selectedCredential
    : null;

  const selectedCredentialInfo = has(formData, 'selectedCredentialInfo')
    ? formData.selectedCredentialInfo
    : null;

  const credentialCreationStatus = has(formData, 'credentialCreationStatus')
    ? formData.credentialCreationStatus
    : { message: null, credential: null };

  const credentialProbeStatus = has(formData, 'credentialProbeStatus')
    ? formData.credentialProbeStatus
    : { message: null, credential: null, isValid: false };

  const addedDataSourceId = has(formData, 'addedDataSourceId') ? formData.addedDataSourceId : null;

  const selectedBucket = has(formData, 'selectedBucket') ? formData.selectedBucket : null;

  const selectedTable = has(formData, 'selectedTable') ? formData.selectedTable : null;

  const selectedCollection = has(formData, 'selectedCollection')
    ? formData.selectedCollection
    : null;

  const sourceConfig = has(formData, 'sourceConfig') ? formData.sourceConfig : null;

  const apiConfig = has(formData, 'apiConfig') ? formData.apiConfig : null;
  const soapConfig = has(formData, 'soapConfig') ? formData.soapConfig : null;
  const templatizedAPIConfig = has(formData, 'templatizedAPIConfig')
    ? formData.templatizedAPIConfig
    : null;
  const nexlaMonitorApiConfig = has(formData, 'nexlaMonitorApiConfig')
    ? formData.nexlaMonitorApiConfig
    : null;

  const templatizedScriptConfig = has(formData, 'templatizedScriptConfig')
    ? formData.templatizedScriptConfig
    : null;

  const scriptCredentialsData = has(formData, 'scriptCredentialsData')
    ? formData.scriptCredentialsData
    : null;

  const selectedCredentialId =
    (selectedCredential && selectedCredential.id) ||
    (credentialCreationStatus && credentialCreationStatus.credential);

  const parentDataSet = dataSets[ownProps.parentDataSetId] || null;

  // Get dataset samples for parentDataSet if it exists
  const parentDataSetSamples = parentDataSet
    ? getSamplesForDataSet(state, { data: parentDataSet })
    : null;

  // Derive connectionCategory from selectedConnection and metaInfoById
  const connectionCategory =
    selectedConnection && paging.metaInfoById[selectedConnection]?.connectionCategory;

  let probeError = null;
  let probeData = null;
  let probeDataFlat = null;
  let probeTreeLastUpdatedAt = null;
  if (selectedCredentialId && has(probe, selectedCredentialId)) {
    const probeResponse = probe[selectedCredentialId];
    probeError = probeResponse.error;

    if (probeResponse.nestedProbeTree) {
      probeData = probeResponse.nestedProbeTree;
    }

    if (probeResponse.tree) {
      probeDataFlat = probeResponse.tree;
    }

    probeTreeLastUpdatedAt = probeResponse.treeLastUpdatedAt;
  }

  // Only check vendors loading when editing a source
  // When creating a new source, vendors loading is handled in SelectConnection
  const isLoadingInitialData =
    (sourceBeingEditedId &&
      (!sourceBeingEdited ||
        !sourceBeingEdited.source_config ||
        (paging.metaInfoById[sourceBeingEdited?.source_type]?.connectionCategory ===
          CONNECTION_CATEGORY_REST &&
          !sourceBeingEdited.vendor &&
          !dataMapsLastUpdated) ||
        !vendorsState.vendors.lastUpdated)) ||
    (sourceBeingEditedId && request[GET_FULL_VENDOR_REQUEST]);
  const isLoadingEndpoints = !vendorsState.endpoints.lastUpdated;
  const isLoadingProbeData = !!request[PROBE_GET_TREE_REQUEST];
  const isLoadingProbeWSDL = !!request[PROBE_GET_WSDL_REQUEST];
  const isLoadingProbeSOAPCall = !!request[PROBE_CALL_SOAP_REQUEST];
  const isLoadingCredentials = !!request[GET_ALL_CREDENTIALS_REQUEST];
  const isLoadingDataMaps = !!request[GET_ALL_DATA_MAPS_REQUEST];

  const probeSample = probe[selectedCredentialId];
  const isLoadingProbeSample = !!request[PROBE_GET_SAMPLE_REQUEST];

  const isCreatingSource = !!request[ADD_DATA_SOURCE_REQUEST];
  const isLoadingDataSource = !!request[GET_DATA_SOURCE_REQUEST];

  // Get initial values from form state
  const initialSourceConfig =
    has(form, 'initialData') && has(form.initialData, 'sourceConfig')
      ? form.initialData.sourceConfig
      : null;
  const initialApiConfig =
    has(form, 'initialData') && has(form.initialData, 'apiConfig')
      ? form.initialData.apiConfig
      : null;

  const currentProjectId = getCurrentProjectId(state);

  // Track if vendors are loading (only when creating new source, not editing)
  const isLoadingVendors = !vendorsState.vendors.lastUpdated;

  return {
    paging,
    connectionCategory,
    selectedFeeds: formData?.selectedFeeds,
    loadingVendorInfo:
      !!request[GET_VENDOR_AUTH_PARAMS_REQUEST] ||
      !!request[GET_VENDOR_ENDPOINTS_REQUEST] ||
      !!request[GET_FULL_VENDOR_REQUEST] ||
      !!request[GET_VENDOR_ENDPOINT_PARAMS_REQUEST],
    isLoadingVendors,
    sourceBeingEditedId,
    sourceBeingEdited,
    credentialsById: byId,
    credentialsByType: getCredentialsByType(state),
    credentials,
    currentUser: getCurrentUser(state),
    currentOrg: getCurrentOrg(state),
    dataMaps: pick(dataMaps, dataMapIds),
    vendors,
    vendorEndpoints,
    dataMapSamples,
    dataMapsLastUpdated,
    currentStage,
    selectedConnection,
    selectedCredentialId,
    selectedCredentialInfo,
    selectedCredential,
    selectedBucket,
    selectedTable,
    apiConfig,
    soapConfig,
    templatizedAPIConfig,
    templatizedScriptConfig,
    scriptCredentialsData,
    sourceConfig,
    credentialCreationStatus,
    credentialProbeStatus,
    addedDataSource: sources[addedDataSourceId],
    probeData,
    probeDataFlat,
    probeError,
    probeTreeLastUpdatedAt,
    isLoadingInitialData,
    isLoadingEndpoints,
    isLoadingProbeData,
    isLoadingProbeWSDL,
    isLoadingProbeSOAPCall,
    isLoadingCredentials,
    isLoadingDataMaps,
    probeSample,
    previousPath,
    isLoadingProbeSample,
    isFormAwaitingValidation,
    isCreatingSource,
    isLoadingDataSource,
    lastFileUploaded,
    nmTemplates: isEmpty(NM_TEMPLATES) ? {} : NM_TEMPLATES,
    nexlaMonitorApiConfig,
    selectedCollection,
    initialSourceConfig,
    initialApiConfig,
    parentDataSet,
    parentDataSetSamples,
    currentProjectId,
  };
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      saveHashedState: saveState,
      loadFullVendor,
      loadDataCredentials,
      loadVendorAuths,
      getDataCredential,
      addCredentialFromForm,
      updateCredentialFromForm,
      verifyCredentialFromForm,
      verifyCredentialSilent,
      addDataSourceFromForm,
      updateDataSourceFromForm,
      updateFormData,
      resetFormData,
      setFormError,
      setFormStageIndex,
      markFormCompleted,
      probeGetConnectionTree,
      probeGetInitialDBTree,
      probeToggleTreeNode,
      probeGetWSDL,
      probeCallSOAP,
      updateSourceInfo,
      uploadFileToSource,
      loadDataMaps,
      loadDataMapSamples,
      probeGetSample,
      probeClearAll,
      probeRemoveRestStage,
      loadDataSource,
      setContextualSupport,
      unsetContextualSupport,
      setFormAwaitingValidation,
      createCodeContainer,
      updateCodeContainer,
      informEarlyIntent,
    },
    dispatch,
  );
}

const ConnectedAddDataSourceForm = connect(mapStateToProps, mapDispatchToProps)(AddDataSourceForm);

function AddDataSourceFormContainer(props) {
  const hideSqlRunnerFromSelect = useSqlAutomation();
  return (
    <ConnectedAddDataSourceForm {...props} hideSqlRunnerFromSelect={hideSqlRunnerFromSelect} />
  );
}

AddDataSourceFormContainer.screenConfig = {
  screenName: 'AddDataSourceFormContainer',
  domain: 'integrate',
  hideSideBar: true,
  pageTitle: 'New Source',
  path: [
    `${ROUTE_REDESIGN}${ROUTE_CONNECT}${OLD_PATHS.ROUTE_DATA_SOURCES}/new`,
    `${ROUTE_REDESIGN}${ROUTE_CONNECT}${OLD_PATHS.ROUTE_DATA_SOURCES}/:id/edit`,
  ],
};

export default AddDataSourceFormContainer;
